import { useMemo, useState } from 'react';
import type { DonationCandidate, DonationCandidateParams } from '../types/marketPromotionP2.types';

interface PickedUsers {
  paramsKey: string;
  userIds: ReadonlySet<number>;
}

const NO_USER_IDS: ReadonlySet<number> = new Set();

export function useDonationCandidatePicks(
  candidates: DonationCandidate[],
  visibleCandidates: DonationCandidate[],
  params: DonationCandidateParams,
) {
  const paramsKey = JSON.stringify(params);
  const [picked, setPicked] = useState<PickedUsers>({ paramsKey, userIds: NO_USER_IDS });
  const pickedUserIds = picked.paramsKey === paramsKey ? picked.userIds : NO_USER_IDS;

  const pickedCandidates = useMemo(
    () => candidates.filter((candidate) => pickedUserIds.has(candidate.userId)),
    [candidates, pickedUserIds],
  );
  const pickedAmount = pickedCandidates.reduce(
    (total, candidate) => total + candidate.donationAmount,
    0,
  );
  const visiblePickedCount = visibleCandidates.filter((candidate) =>
    pickedUserIds.has(candidate.userId),
  ).length;

  function updatePickedUserIds(update: (userIds: Set<number>) => void) {
    const next = new Set(pickedUserIds);
    update(next);
    setPicked({ paramsKey, userIds: next });
  }

  function togglePick(candidate: DonationCandidate) {
    updatePickedUserIds((userIds) => {
      if (userIds.has(candidate.userId)) userIds.delete(candidate.userId);
      else userIds.add(candidate.userId);
    });
  }

  function toggleAllVisible(shouldPick: boolean) {
    updatePickedUserIds((userIds) => {
      for (const candidate of visibleCandidates) {
        if (shouldPick) userIds.add(candidate.userId);
        else userIds.delete(candidate.userId);
      }
    });
  }

  return {
    pickedCandidates,
    pickedAmount,
    isPicked: (candidate: DonationCandidate) => pickedUserIds.has(candidate.userId),
    togglePick,
    toggleAllVisible,
    isEveryVisiblePicked:
      visibleCandidates.length > 0 && visiblePickedCount === visibleCandidates.length,
    isSomeVisiblePicked: visiblePickedCount > 0 && visiblePickedCount < visibleCandidates.length,
  };
}
